import { Typography } from '@mui/material';
import { isNumber } from '@shared/utils/number';
import { Flex } from '@/component/Flex';
import Icon from '@/component/Icon';
import IconType from '@/component/Icon/IconType';
import { Parameter } from '@/component/ParameterList';
import Tooltip from '@/component/Tooltip';
import { Value } from '@/component/Value';
import type { Precision } from '@/component/Value/types';
import { useTexts } from '@/feature/lang';
import { getSignalPrecision } from '@/feature/signals/metadata';
import { useSignalMetadataQuery } from '@/feature/signals/useSignalMetadataQuery';
import type { Alarm } from '../types';

interface ValueParameterProps {
  alarm: Alarm | undefined;
}

export const ValueParameter = ({ alarm }: ValueParameterProps) => {
  const texts = useTexts().fleet.alarms;
  const { data: metadata } = useSignalMetadataQuery(alarm?.equipmentId);

  const precision = getSignalPrecision(
    alarm?.breachDetails?.signalNames.at(0),
    metadata
  );

  if (!alarm?.breachDetails) {
    return null;
  }

  return (
    <Parameter label={texts.model.breachValue}>
      <Flex>
        <Value
          value={alarm.breachDetails.breachValue}
          unit={alarm.breachDetails.ucumCode}
          precision={precision}
        />
        <Thresholds alarm={alarm} precision={precision} />
      </Flex>
    </Parameter>
  );
};

const Thresholds = ({
  alarm,
  precision,
}: ValueParameterProps & { precision?: Precision }) => {
  const texts = useTexts().fleet.alarms.details;
  const lower = alarm?.breachDetails?.alarmOnOrBelow;
  const upper = alarm?.breachDetails?.alarmOnOrAbove;

  if (!isNumber(lower) && !isNumber(upper)) {
    return null;
  }

  return (
    <Flex sx={{ display: 'inline-flex', marginLeft: 2 }} gap={1}>
      {isNumber(lower) && (
        <Tooltip title={texts.thresholdLowerTooltip}>
          <Flex gap={0.25}>
            <Icon type={IconType.ThresholdLower} size="sm" />
            <Typography variant="caption" color="text.secondary">
              <Value value={lower} precision={precision} />
            </Typography>
          </Flex>
        </Tooltip>
      )}
      {isNumber(upper) && (
        <Tooltip title={texts.thresholdUpperTooltip}>
          <Flex gap={0.25}>
            <Icon type={IconType.ThresholdUpper} size="sm" />
            <Typography variant="caption" color="text.secondary">
              <Value value={upper} precision={precision} />
            </Typography>
          </Flex>
        </Tooltip>
      )}
    </Flex>
  );
};
